<template>
  <div
    v-if="sample != null"
    class="paper-block-container paper-block-container--main sample-component"
  >
    <ImportCsvModal
      :sampleId="sample.id"
      :method="method"
      :column="column"
      @createInjection="handleInjectionCreation"
    />

    <div class="paper__top">
      <div class="paper__header">
        <div>
          <InputTitle
            id="block-name"
            v-model="sample.name"
            iconName="opacity"
            data-test-id="sample-name"
            @save="saveName"
          />

          <div class="pt-1">
            <template v-if="sample.was_calibration">
              <span class="sample-component__warning"
                >This sample was originally created as a calibration. <br />
                It was converted to a sample to maintain compatibility after a major software
                upgrade.</span
              >
              <br /><br />
            </template>
            <span class="mr-3">Sample</span>

            <span class="span-date mr-3" v-html="new Date(sample.created).toLocaleDateString()" />

            <div v-if="sample.device_id != null" style="margin-right: 12px; display: inline">
              <router-link :to="linkToDevice">
                <IconMaterial title="input" :size="16" />
                {{ sample.device_name }}</router-link
              >
            </div>

            <StateArchived v-if="sample.archived" style="margin-right: 12px" />

            <ShareableLinkComponent
              :url="shareableLink"
              @action:share="share"
              @action:stopSharing="stopSharing"
            />
          </div>
        </div>
        <div class="sample-component__button-group">
          <Btn
            v-if="sample.archived"
            type="primary-transparent"
            icon-left="restore"
            padding="xs"
            height="s"
            class="sample-component__action_button"
            data-test-id="btn-restore"
            @click="restore"
          >
            Restore
          </Btn>
          <button v-else-if="isEditMode" class="button--square-image" @click="finishEditing">
            <i class="material-icons material-icon--18">done</i>
            Finish <br />
            editing
          </button>

          <div
            v-show="!isEditMode && !sample.archived"
            class="sample-component__button-column"
            data-test-id="btn-more-menu"
          >
            <Btn
              type="primary-transparent"
              icon-left="vertical_align_top"
              padding="xs"
              height="s"
              class="sample-component__action_button"
              @click="openImportCsv"
            >
              Import
            </Btn>
            <Btn
              type="primary-transparent"
              icon-left="edit"
              padding="xs"
              height="s"
              class="sample-component__action_button"
              @click="startEditing"
            >
              Edit
            </Btn>
            <div v-if="isPaused" v-tippy content="Sample is paused! Stop it to archive!">
              <Btn
                type="primary-transparent"
                icon-left="delete"
                padding="xs"
                height="s"
                class="sample-component__action_button"
                data-test-id="btn-archive"
                disabled
              >
                Archive
              </Btn>
            </div>
            <Btn
              v-else-if="sample.can_archive && !isInProgress"
              type="primary-transparent"
              icon-left="delete"
              padding="xs"
              height="s"
              class="sample-component__action_button"
              data-test-id="btn-archive"
              @click="archive"
            >
              Archive
            </Btn>
          </div>
        </div>
      </div>
      <div class="paper__content--margin-32">
        <ModelTextareaComponent
          ref="description"
          v-model="sample.description"
          :isEditMode="isEditMode"
          :label="'Description'"
          :canEdit="sample.can_edit"
          style="margin: 16px 0 0"
          data-test-id="sample-description"
          @click="startEditing"
          @update:value="saveDescription"
        />
      </div>
    </div>

    <SampleInjectionsListComponent
      v-show="injectionsAll"
      :injections="injections"
      :injectionID="injectionID"
      :archived="showArchivedInjections"
      :allowNew="sample.sequence_id == null"
      :isInProgress="isInProgress"
      hasArchiveToggle
      @update:injectionID="showInjection($event)"
      @update:archived="showArchivedInjections = $event"
    />
    <div v-show="!injectionsAll" style="padding-bottom: 32px">
      <InjectionsNavBarComponent
        :injections="injections"
        :injectionID="injectionID"
        :archived="showArchivedInjections"
        :allowNew="sample.sequence_id == null"
        :isInProgress="isInProgress"
        @update:injectionID="showInjection($event)"
        @update:archived="showArchivedInjections = $event"
      />

      <SampleInjectionComponent
        v-if="injectionData"
        v-show="!injectionNew"
        ref="injection"
        :injectionData="injectionData"
        :sample="sampleData"
        :sampleSocket="sampleSocket"
        :canEditChromatogram="!isCalibrationSequenceSample"
        :isVisible="isVisible"
      />

      <template v-if="injectionNew">
        <InjectionNewComponent
          v-if="sample.can_edit"
          :sampleId="sample.id"
          :injection-name="(sample.injections_counter + 1).toString()"
          :sample-socket="sampleSocket"
          :default-vial="defaultVial"
          :default-device="
            sample.device_id ? { id: sample.device_id, name: sample.device_name } : undefined
          "
          :default-method="method"
          :default-column="column"
          @injectionCreate="handleInjectionCreation"
        />
        <NotAvailableComponent v-else class="pt-8">
          Finished or rejected samples can not run new injections.
        </NotAvailableComponent>
      </template>
    </div>
  </div>
  <ErrorComponent v-else-if="errorCode != null" :code="errorCode" class="mt-16" />
  <LoadingComponent v-else />
</template>

<script>
  import 'assets/css/base/table.scss';
  import _ from 'lodash';

  import StateArchived from '@/uikitProject/states/StateArchived';
  import ErrorComponent from 'components/element/ErrorComponent';
  import ImportCsvModal from 'components/block/modal/ImportCsvModal';
  import InjectionNewComponent from 'components/block/InjectionNewComponent';
  import InjectionsNavBarComponent from 'components/block/InjectionsNavBarComponent';
  import LoadingComponent from 'components/element/LoadingComponent';
  import ModelTextareaComponent from 'components/element/ModelTextareaComponent';
  import Btn from '@/uikitBase/btns/Btn';
  import NotAvailableComponent from 'components/element/NotAvailableComponent';
  import SampleInjectionComponent from 'components/block/SampleInjectionComponent';
  import SampleInjectionsListComponent from 'components/block/SampleInjectionsListComponent';
  import SampleSocket, { SampleSocketEvents } from 'api/sockets/SampleSocket';
  import ShareableLinkComponent from 'components/element/ShareableLinkComponent';
  import InputTitle from '@/uikitProject/inputs/inputTitle/InputTitle';
  import IconMaterial from '@/uikitBase/icons/IconMaterial';
  import { isSampleInProgress } from 'utils/sampleHelpers';
  import { apiSamples } from '@/api/graphql/cloud/samples';
  import { apiInjections } from '@/api/graphql/cloud/injections';
  import DataNotFoundError from '@/errors/DataNotFoundError';

  const EVENT_UPDATE_INJECTION_ID = 'update:injectionID';
  const EVENT_UPDATE_SAMPLE_DATA = 'update:sampleData';
  const EVENT_ARCHIVE = 'archive';

  export default {
    name: 'SampleComponent',

    components: {
      IconMaterial,
      InputTitle,
      ShareableLinkComponent,
      ImportCsvModal,
      ErrorComponent,
      NotAvailableComponent,
      SampleInjectionsListComponent,
      InjectionsNavBarComponent,
      LoadingComponent,
      ModelTextareaComponent,
      Btn,
      StateArchived,
      InjectionNewComponent,
      SampleInjectionComponent,
    },

    props: {
      id: Number,
      injectionID: Number,
      sampleData: {
        type: Object,
        default: () => {
          //stub
        },
      },
      isPaused: {
        type: Boolean,
      },
      isVisible: {
        type: Boolean,
        default: true,
      },
    },

    data() {
      return {
        sample: undefined,
        device: undefined,
        method: undefined,
        column: undefined,
        injections: {},
        injectionData: null,
        sampleSocket: this.createSocket(this.id),
        showArchivedInjections: false,
        errorCode: null,
        importCsvVisible: false,

        isEditMode: false,

        listenersGroupId: null,

        subscriptionForSampleBrief: null,
        subscriptionForInjectionsState: null,
      };
    },

    computed: {
      injectionsAll: {
        get() {
          return this.injectionID == null;
        },
        set(b) {
          if (b) this.showInjection(null);
        },
      },
      injectionNew: {
        get() {
          return this.injectionID === -1;
        },
        set(b) {
          if (b) this.showInjection(-1);
        },
      },
      defaultVial() {
        if (this.injections == null) return undefined;
        const ids = Object.keys(this.injections);
        if (ids.length <= 0) return undefined;
        return this.injections[ids[ids.length - 1]].injection.vial;
      },
      shareableLink() {
        if (this.sample.token_hex) {
          const token = this.sample.token_hex.replaceAll('-', '');

          let url = `samples/${this.id}`;
          if (this.injectionID && this.injectionID > 0) {
            url += `/injection/${this.injectionID}`;
          }
          return `${window.location.origin}/share/#/${url}/?t=${token}`;
        }
        return null;
      },

      linkToDevice() {
        return {
          name: 'device',
          params: { id: this.sample.device_id },
        };
      },

      isInProgress() {
        return isSampleInProgress(this.sample);
      },

      isCalibrationSequenceSample() {
        return this.sample.sequence?.is_calibration_mode;
      },
    },

    watch: {
      id(id) {
        this.errorCode = null;
        this.sampleSocket = this.createSocket(id);
        this.initData(id);
        this.initSubscriptions(id);
      },
      injectionID() {
        this.updateInjectionData();
      },
      injections() {
        this.updateInjectionData();

        if (!Object.values(this.injections).length) {
          this.startEditing();
        } else {
          this.finishEditing();
        }
      },
      sampleData(newData) {
        if (newData == null) return;
        if (newData.id !== this.id) return;
        if (this.sample == null || this.sample.id !== newData.id) {
          this.sample = newData;
        }
      },
    },

    created() {
      this.initData(this.id);
      this.initSubscriptions(this.id);
    },

    beforeDestroy() {
      this.destroySocket();
      this.stopSubscriptions();
    },

    methods: {
      async initData(sampleId) {
        try {
          const data = await apiSamples.getSampleFull(sampleId);

          if (data.sample.type === 'standard') {
            this.notifyError(
              'This is a standard. Go to the corresponding sequence page to see it!',
            );
            this.errorCode = 403;
            return;
          }

          if (_.has(data, 'sample')) {
            this.setSample(data.sample);
          }
          if (_.has(data, 'device')) {
            this.device = data.device;
          }
          if (_.has(data, 'method')) {
            this.method = data.method;
          }
          if (_.has(data, 'column')) {
            this.column = data.column;
          }
          if (_.has(data, 'injections')) {
            this.injections = data.injections;
          }
        } catch (e) {
          if (e instanceof DataNotFoundError) {
            this.resetData();
          }
          throw e;
        }
      },

      resetData() {
        this.setSample(undefined);
        this.device = undefined;
        this.method = undefined;
        this.column = undefined;
        this.injections = {};
      },

      setSample(s) {
        // eslint-disable-next-line vue/no-mutating-props
        this.sampleData = {
          ...this.sampleData,
          ...s,
        };
        this.sample = this.sampleData;
        this.$emit(EVENT_UPDATE_SAMPLE_DATA, s);
      },

      destroySocket() {
        if (this.sampleSocket) {
          this.sampleSocket.close(this.listenersGroupId);
          this.sampleSocket = null;
        }
      },
      createSocket(id) {
        this.destroySocket();

        const sampleSocket = SampleSocket.start(id, null, (e) => {
          this.errorCode = e.code;
        });

        const listenersGroup = sampleSocket.createEventListenersGroup();
        this.listenersGroupId = listenersGroup.id;

        const onSampleRefresh = (sampleID) => {
          this.initData(sampleID);
          this.$refs.injection.resetZoom();
        };

        listenersGroup.addEventListener(SampleSocketEvents.SAMPLE_REFRESH, onSampleRefresh);

        return sampleSocket;
      },

      updateInjectionData() {
        if (this.injections[this.injectionID] != null) {
          this.injectionData = this.injections[this.injectionID];
        }
      },

      saveName(name) {
        this.showNotificationIfRpcError(() => this.sampleSocket.update({ name }));
      },
      saveDescription() {
        this.showNotificationIfRpcError(() =>
          this.sampleSocket.update({ description: this.sample.description }),
        );
      },

      startEditing() {
        if (this.sample?.can_edit) {
          this.isEditMode = true;
        }
      },
      finishEditing() {
        if (this.isEditMode) {
          this.$refs.description.save();
          this.isEditMode = false;
        }
      },

      archive() {
        this.showNotificationIfRpcError(() => this.sampleSocket.archive());

        this.$emit(EVENT_ARCHIVE);
      },
      restore() {
        this.showNotificationIfRpcError(() => this.sampleSocket.restore());
      },

      share() {
        this.showNotificationIfRpcError(() => this.sampleSocket.share());
      },

      stopSharing() {
        this.showNotificationIfRpcError(() => this.sampleSocket.stopSharing());
      },

      showInjection(iid) {
        this.$emit(EVENT_UPDATE_INJECTION_ID, iid);
      },

      openImportCsv() {
        this.$modal.show('import-csv');
      },

      async handleInjectionCreation(newInjection) {
        console.warn('handleInjectionCreation', newInjection);
        this.$set(this.injections, newInjection.id, {
          injection: newInjection,
          device: newInjection.device,
          method: newInjection.method,
          column: newInjection.column,
        });

        await this.$nextTick();
        this.showInjection(newInjection.id);
      },

      initSubscriptions(sampleId) {
        this.stopSubscriptions();
        this.initSubscriptionForSampleBrief(sampleId);
        this.initSubscriptionForInjectionsState(sampleId);
      },

      initSubscriptionForSampleBrief(sampleId) {
        this.subscriptionForSampleBrief = apiSamples.subscribeForSampleBrief(sampleId, (sample) => {
          this.setSample(sample);
        });
      },

      initSubscriptionForInjectionsState(sampleId) {
        this.subscriptionForInjectionsState = apiInjections.subscribeForInjectionsState(
          sampleId,
          (injections) => {
            let hasUpdates = false;

            injections.forEach((injectionUpdate) => {
              const existing = this.injections[injectionUpdate.id];

              if (existing) {
                this.injections[injectionUpdate.id] = {
                  ...existing,
                  injection: {
                    ...existing.injection,
                    state: injectionUpdate.state,
                  },
                };
                hasUpdates = true;
              }
            });

            if (hasUpdates) {
              this.updateInjectionData();
            }
          },
        );
      },

      stopSubscriptions() {
        this.subscriptionForSampleBrief?.unsubscribe();
        this.subscriptionForInjectionsState?.unsubscribe();
      },
    },
  };
</script>

<style lang="scss" scoped>
  .sample-component {
    &__warning {
      color: $color-text-warning;
    }

    &__button-group {
      display: flex;
      flex-direction: row;
      gap: 4px;

      @media (max-width: $screen-sm-max) {
        flex-direction: column;
      }
    }

    &__button-column {
      display: flex;
      flex-direction: row;
      gap: 4px;

      @media (max-width: $screen-sm-max) {
        flex-direction: column;
      }
    }

    &__action_button {
      justify-content: start;
    }
  }
</style>
